import type { FeatureFlagKey } from '../../../../config/featureFlags';
import { adRouteFromPath, type AdTab } from '../../asset-routes';
import type { TrackingLinkKind } from '../../tracking-links-api';
import { videoRouteFromPath } from '../../video-routes';

/**
 * The in-place asset overlay's URL marker and helpers. Opening an asset in place (Home's top assets, the Players page's
 * "came from") adds `?asset=<kind>:<creativeId>` to the CURRENT page's address, so a reload or back / forward keeps the
 * overlay; closing it removes the marker. Pure (no React, no router), unit-testable in the node jest env.
 */

/** The query key of the marker. */
export const ASSET_OVERLAY_PARAM = 'asset';

/** An asset to open: its link kind and its spark-creatives id. */
export interface AssetRef {
  kind: TrackingLinkKind;
  id: string;
}

/** The Marketing Assets area gate every asset route carries (see router.tsx `assetGated`). */
export const MARKETING_ASSETS_FLAG: FeatureFlagKey = 'creatives-assets';

/** The flag each asset kind's composer route is gated on (router.tsx `assetGated`). */
export const ASSET_KIND_FLAG: Record<TrackingLinkKind, FeatureFlagKey> = {
  video: 'creatives-video',
  kit: 'creatives-banner-kits',
  playable: 'creatives-playables',
  banner: 'creatives-html5-banners',
  landing_page: 'creatives-landing-pages',
};

const KINDS: readonly TrackingLinkKind[] = ['video', 'kit', 'playable', 'banner', 'landing_page'];

const isKind = (value: string): value is TrackingLinkKind => (KINDS as readonly string[]).includes(value);

/** Whether an asset kind can open (its route's gates are both on), by a flag predicate. Pure. */
export const assetKindOpens = (kind: TrackingLinkKind, isEnabled: (flag: FeatureFlagKey) => boolean): boolean =>
  isEnabled(MARKETING_ASSETS_FLAG) && isEnabled(ASSET_KIND_FLAG[kind]);

/** The marker's value for an asset. Pure. */
export const formatAssetMarker = (ref: AssetRef): string => `${ref.kind}:${ref.id}`;

/** The asset a marker names, or null (no marker, an unknown kind, or no id). Split on the FIRST colon, so an id may
 *  hold one. Pure. */
export const parseAssetMarker = (raw: string | null | undefined): AssetRef | null => {
  const value = (raw ?? '').trim();
  const at = value.indexOf(':');
  if (at <= 0) return null;
  const kind = value.slice(0, at);
  const id = value.slice(at + 1).trim();
  return isKind(kind) && id ? { kind, id } : null;
};

const KIND_OF_TAB: Record<AdTab, TrackingLinkKind> = {
  'banner-kits': 'kit',
  'html5-banners': 'banner',
  playables: 'playable',
  'landing-pages': 'landing_page',
};

/** The asset an asset composer path names (`assetPath`, either video tab included), or null for any other path (the
 *  Affiliates page, a library grid, a "new"). Its query and hash are ignored. Pure. */
export const assetRefFromPath = (path: string): AssetRef | null => {
  const pathname = path.split(/[?#]/, 1)[0] ?? '';
  const video = videoRouteFromPath(pathname);
  if (video) return video.view.kind === 'edit' ? { kind: 'video', id: video.view.id } : null;
  const ad = adRouteFromPath(pathname);
  if (ad && ad.view.kind === 'edit') return { kind: KIND_OF_TAB[ad.tab], id: ad.view.id };
  return null;
};
